// libraries.
import { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { MapContext, PolygonF } from '@react-google-maps/api';

// components.
import { PolygonIdLabel } from '@domains/google-map';

// api.
import { useGetIntersectionAuditQuery } from '@domains/audit-intersections/store/api-slices/auditIntersections.endpoints';

// slices.
import {
  getAuditIntersectionsRequested,
  getSelectedMissingIntersection,
} from '@domains/audit-intersections/store/slices/auditIntersections.slice';
import { getCountry } from '@domains/user/store/slices/user-slice/user-slice';
import { getListPolygons } from '@domains/save-polygons';

// clickable:false es obligatorio: cuando el polygon si esta cargado en el mapa,
// este overlay queda EXACTAMENTE encima del que renderiza ListPolygons, con
// zIndex mayor. Si fuera clickable se traga el mousedown de los handles de
// edicion y el polygon deja de ser editable mientras esta resaltado.
const HIGHLIGHT_OPTIONS: google.maps.PolygonOptions = {
  clickable: false,
  fillColor: '#FD7E14',
  fillOpacity: 0.25,
  strokeColor: '#FD7E14',
  strokeWeight: 3,
  zIndex: 200,
};

export const AuditSelectedIntersection = () => {
  const map = useContext(MapContext);
  const selected = useSelector(getSelectedMissingIntersection);
  const requested = useSelector(getAuditIntersectionsRequested);
  const country = useSelector(getCountry);
  const listPolygons = useSelector(getListPolygons);

  // Misma cache que AuditIntersectionsTool (RTK Query dedupe). Se suscribe aqui
  // —y no solo en el tool— porque este componente sigue montado aunque el
  // usuario se cambie de pestania: al borrar un polygon, savePolygons invalida
  // el tag, la auditoria se refresca y el par desaparece del reporte.
  const { data } = useGetIntersectionAuditQuery(
    { country },
    { skip: !requested }
  );

  // Si el reporte ya no trae el par (p.ej. se borro uno de los dos polygons),
  // no hay nada que resaltar: sin esto quedaba pintado un polygon inexistente.
  const stillReported =
    !data ||
    data.missing.some(
      item =>
        item.p_min === selected?.p_min && item.p_max === selected?.p_max
    );

  // La geometria VIVA del mapa manda: asi el resaltado sigue al polygon
  // mientras se edita, en vez de dejar un fantasma debajo. La geometria que
  // devuelve el endpoint es solo el respaldo para pares cuya zona mediana no
  // existe (data de prueba), que por eso nunca cargan en el mapa.
  const pathFor = (
    id: number,
    fallback: google.maps.LatLngLiteral[] | undefined
  ) => {
    const live = listPolygons.find(
      polygon => String(polygon.polygon_id) === String(id)
    );
    return live?.polygon ?? fallback ?? [];
  };

  const pair =
    selected && stillReported
      ? [
          { id: String(selected.p_min), path: pathFor(selected.p_min, selected.polygon_min) },
          { id: String(selected.p_max), path: pathFor(selected.p_max, selected.polygon_max) },
        ].filter(member => member.path.length > 0)
      : [];

  useEffect(() => {
    if (map && pair.length) {
      const bounds = new window.google.maps.LatLngBounds();
      pair.forEach(member => member.path.forEach(coord => bounds.extend(coord)));
      map.fitBounds(bounds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  if (!pair.length) return null;

  return (
    <>
      {pair.map(member => (
        <PolygonF
          key={member.id}
          path={member.path}
          options={HIGHLIGHT_OPTIONS}
        />
      ))}
      {pair.map(member => (
        <PolygonIdLabel
          key={member.id}
          id={member.id}
          path={member.path}
          color="#FD7E14"
        />
      ))}
    </>
  );
};
